import { Component, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent {
  private authService = inject(AuthService);
  private router = inject(Router);

  email = '';
  loading = false;
  errorMessage = '';

  /**
   * True when errorMessage is the "no account found" rejection, lets the
   * template render "register" as a clickable inline link instead of plain
   * text.
   */
  get showRegisterCta(): boolean {
    return /register first/i.test(this.errorMessage);
  }

  /** Splits errorMessage around the word "register" for the inline-link template. */
  get errorParts(): { text: string; isLink: boolean }[] {
    return this.errorMessage
      .split(/(register)/i)
      .filter((part) => part.length > 0)
      .map((part) => ({ text: part, isLink: /^register$/i.test(part) }));
  }

  /**
   * There's no way to know which profile type (candidate/panel/employer)
   * an email that was never registered would have belonged to, so this
   * just goes to the general register page, preselected on whichever tab
   * was last chosen ('ip-auth-role' in localStorage, the same value
   * every other register/login link in the app already relies on).
   */
  goToRegister(): void {
    this.router.navigate(['/auth/register']);
  }

  next() {
    const trimmed = this.email.trim();
    if (!trimmed) {
      this.errorMessage = 'Please enter your email address.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    // Clear any stale session tokens so the interceptor never attaches them
    // to unauthenticated OTP requests (stale JWT would cause 401 on the backend).
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Step 1: check the email is registered before sending an OTP
    this.authService.checkEmailExists(trimmed).subscribe({
      next: ({ exists }) => {
        if (!exists) {
          this.loading = false;
          this.errorMessage = 'No account found with this email. Please register first.';
          return;
        }

        // Step 2: email exists, send the OTP. purpose: 'forgot-password'
        // also makes the backend itself re-check the account exists right
        // before generating the OTP (EmailOtpController#generateOtp),
        // this client-side checkEmailExists() call above is just the fast
        // path/UX; the backend check is what actually can't be bypassed.
        // That check always returns HTTP 200, rejection is reported via
        // res.success === false, not the error channel.
        this.authService.generateOtp(trimmed, 'forgot-password').subscribe({
          next: (res) => {
            this.loading = false;
            if (!res.success) {
              this.errorMessage = res.message || 'No account found with this email.';
              return;
            }
            localStorage.setItem('verifyEmail', trimmed);
            localStorage.setItem('authFlow', 'forgotPassword');
            this.router.navigate(['/auth/verify-otp']);
          },
          error: (err: any) => {
            this.loading = false;
            this.errorMessage = this._extractError(
              err,
              'Failed to send verification code. Please try again.',
            );
          },
        });
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'Could not verify email. Please try again.';
      },
    });
  }

  /**
   * Fallback for genuine transport-level errors (network failure, 5xx,
   * etc.), the "no account found" case itself no longer reaches here,
   * generateOtp() reports that via a 200 response with success: false
   * (see the next() handler above), not the error channel.
   */
  private _extractError(err: any, fallback: string): string {
    let body = err?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return body.trim() || fallback;
      }
    }
    return body?.message || body?.error || fallback;
  }
}
